import { useState, useEffect, useCallback, useRef } from 'react';
import { getInsightsWsToken, type ProcessingPhase, type ProcessingProgress, type ProcessingResult } from '@/api/insights';

interface CallProcessorState {
  isConnected: boolean;
  isProcessing: boolean;
  progress: ProcessingProgress | null;
  result: ProcessingResult | null;
  error: string | null;
}

interface UseCallProcessorOptions {
  projectId: string;
  buildId: number;
  onComplete?: (result: ProcessingResult) => void;
  onError?: (error: string) => void;
}

export function useCallProcessor({
  projectId,
  buildId,
  onComplete,
  onError,
}: UseCallProcessorOptions) {
  const [state, setState] = useState<CallProcessorState>({
    isConnected: false,
    isProcessing: false,
    progress: null,
    result: null,
    error: null,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 3;

  const connect = useCallback(async () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      // Get auth token
      const { token } = await getInsightsWsToken(projectId, buildId);

      // Determine WebSocket URL
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = import.meta.env.PROD
        ? `wss://odoo-ai-agent.doploy.workers.dev/calls/${projectId}/${buildId}?token=${token}`
        : `${protocol}//${window.location.hostname}:8787/calls/${projectId}/${buildId}?token=${token}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[CallProcessor] WebSocket connected');
        reconnectAttempts.current = 0;
        setState(prev => ({ ...prev, isConnected: true, error: null }));
      };

      ws.onclose = (event) => {
        console.log('[CallProcessor] WebSocket closed:', event.code, event.reason);
        setState(prev => ({ ...prev, isConnected: false }));

        // Attempt reconnect if not intentional close
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          setTimeout(connect, 1000 * reconnectAttempts.current);
        }
      };

      ws.onerror = (error) => {
        console.error('[CallProcessor] WebSocket error:', error);
        setState(prev => ({ ...prev, error: 'Connection error' }));
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleMessage(message);
        } catch (err) {
          console.error('[CallProcessor] Failed to parse message:', err);
        }
      };
    } catch (err) {
      console.error('[CallProcessor] Failed to connect:', err);
      setState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to connect',
      }));
    }
  }, [projectId, buildId]);

  const handleMessage = useCallback((message: {
    type: string;
    phase?: ProcessingPhase;
    progress?: number;
    message?: string;
    callId?: string;
    error?: string;
    result?: ProcessingResult;
  }) => {
    switch (message.type) {
      case 'pong':
        // Heartbeat response
        break;

      case 'progress':
        setState(prev => ({
          ...prev,
          progress: {
            phase: message.phase!,
            progress: message.progress!,
            message: message.message!,
          },
        }));
        break;

      case 'complete':
        setState(prev => ({
          ...prev,
          isProcessing: false,
          progress: {
            phase: 'complete',
            progress: 100,
            message: 'Processing complete!',
          },
          result: message.result!,
        }));
        onComplete?.(message.result!);
        break;

      case 'error':
        setState(prev => ({
          ...prev,
          isProcessing: false,
          error: message.error || 'Processing failed',
        }));
        onError?.(message.error || 'Processing failed');
        break;
    }
  }, [onComplete, onError]);

  const processCall = useCallback((callId: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setState(prev => ({ ...prev, error: 'Not connected' }));
      return;
    }

    setState(prev => ({
      ...prev,
      isProcessing: true,
      progress: {
        phase: 'uploading',
        progress: 0,
        message: 'Starting...',
      },
      result: null,
      error: null,
    }));

    wsRef.current.send(JSON.stringify({
      type: 'process',
      callId,
    }));
  }, []);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }
  }, []);

  // Ping to keep connection alive
  useEffect(() => {
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => clearInterval(pingInterval);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    ...state,
    connect,
    disconnect,
    processCall,
  };
}
